import Handlebars from "handlebars";
import { sortBy } from "lodash-es";
import * as Log from "../helpers/browserConsole";
import { format } from "date-fns";
import { da } from "date-fns/locale/da";
import { enUS } from "date-fns/locale/en-US";

export function RegisterHelpers() {
  Handlebars.registerHelper("relatedTo", function (module, typeNames, options) {
    if (typeNames === undefined) {
      Log.warn(`No module type specified"`);
      return [];
    }
    if (typeof typeNames === "string") {
      var typeNamesArray = [typeNames];
    } else {
      var typeNamesArray = Object.values(typeNames);
    }
    var allFilteredArrays = typeNamesArray.flatMap((typeName) => {
      var dataSetModule = options.data.root.relatedModules[typeName];
      if (dataSetModule === undefined) {
        Log.warn(`No module set with type: "${typeName}"`);
        return [];
      }
      var relatedItems = module.relations;
      var filteredArray = relatedItems
        .filter((relation) => relation.postType == typeName)
        .map((relation) =>
          dataSetModule.elements.find(
            (otherModule) => otherModule.id == relation.id
          )
        )
        .filter((x) => x !== undefined);
      return filteredArray;
    });
    return allFilteredArrays;
  });

  Handlebars.registerHelper(
    "getElementByPostTypeAndId",
    function (postType, id, options) {
      // Access the relations array from the context
      var relations = this.relations;

      // Find the relation object with the specified postType and id
      var relation = relations.find(function (rel) {
        return rel.postType === postType && rel.id == id;
      });

      // If found, use the provided options to render the content
      if (relation) {
        return options.fn(relation);
      } else {
        return options.inverse(this);
      }
    }
  );

  Handlebars.registerHelper("locationsOf", function (module, options) {
    var locations = options.data.root.relatedModules["location"];
    if (locations === undefined) {
      Log.warn(`No module set with type: "${typeName}"`);
      return [];
    }
    var locationsOf = module.locations;
    var filteredLocations = locationsOf
      .map((locationLink) =>
        locations.find((location) => location.id == locationLink.id)
      )
      .filter((x) => x !== undefined);
    return filteredLocations;
  });

  Handlebars.registerHelper("getLocation", function (locationId, options) {
    var locations = options.data.root.relatedModules["location"];
    if (locations.elements === undefined) {
      Log.warn(`No locations found`);
      return [];
    }
    if (locationId === undefined) {
      Log.warn(`Location ID missing`);
    }
    var result = locations.elements.find(
      (location) => location.id == locationId
    );

    if (result !== undefined && result.locationData.id) {
      return result.locationData;
    }
  });

  function parseAccessor(accessor) {
    const path = accessor.split(".");
    const keys = path.map((key) => {
      if (key[0] == "[" && key[key.length - 1] == "]") {
        key = key.substring(1, key.length - 1);
      }
      return key;
    });
    // return a function that given an object, follows the accessor path and returns the value
    return function (object) {
      return keys.reduce((prev, cur) => prev && prev[cur], object);
    };
  }

  Handlebars.registerHelper("sortedBy", function (accessor, list, options) {
    const accessorFunc = parseAccessor(accessor);
    return sortBy(list, [accessorFunc]);
  });

  Handlebars.registerHelper("get", function (object, accessor, options) {
    const accessorFunc = parseAccessor(accessor);
    return accessorFunc(object);
  });

  Handlebars.registerHelper("ifEquals", function (arg1, arg2, options) {
    return arg1 == arg2 ? options.fn(this) : options.inverse(this);
  });

  Handlebars.registerHelper("ifNotEquals", function (arg1, arg2, options) {
    return arg1 != arg2 ? options.fn(this) : options.inverse(this);
  });

  Handlebars.registerHelper("ifLess", function (arg1, arg2, options) {
    return arg1 < arg2 ? options.fn(this) : options.inverse(this);
  });

  Handlebars.registerHelper("ifGreater", function (arg1, arg2, options) {
    return arg1 > arg2 ? options.fn(this) : options.inverse(this);
  });

  Handlebars.registerHelper(
    "ifLessAndGreater",
    function (value, less, greater, options) {
      return value < less && value > greater
        ? options.fn(this)
        : options.inverse(this);
    }
  );

  Handlebars.registerHelper("add", function (left, right) {
    return left + right;
  });

  Handlebars.registerHelper("ifObject", function (item, options) {
    if (typeof item === "object") {
      return options.fn(this);
    } else {
      return options.inverse(this);
    }
  });

  Handlebars.registerHelper("arraySize", function (data) {
    return Object.keys(data).length;
  });

  const reduceOp = function (args, reducer) {
    args = Array.from(args);
    args.pop(); // => options
    var first = args.shift();
    return args.reduce(reducer, first);
  };

  Handlebars.registerHelper({
    eq: function () {
      return reduceOp(arguments, (a, b) => a === b);
    }, //equal
    ne: function () {
      return reduceOp(arguments, (a, b) => a !== b);
    }, //not equal
    lt: function () {
      return reduceOp(arguments, (a, b) => a < b);
    }, // less than
    gt: function () {
      return reduceOp(arguments, (a, b) => a > b);
    }, // greater than
    lte: function () {
      return reduceOp(arguments, (a, b) => a <= b);
    }, // less than and equal
    gte: function () {
      return reduceOp(arguments, (a, b) => a >= b);
    }, // greater than and equal
    and: function () {
      return reduceOp(arguments, (a, b) => a && b);
    }, // and
    or: function () {
      return reduceOp(arguments, (a, b) => a || b);
    }, // or
  });

  // Handlebars helper: Contains
  // check if a value is contained in an array
  Handlebars.registerHelper("contains", function (value, array, options) {
    // fallback...
    array = array instanceof Array ? array : [array];
    if (array.indexOf(value) > -1) {
      return options.fn(this);
    } else {
      return options.inverse(this);
    }
  });

  // Handlebars helper: notContains
  // Check if a value is NOT contained in an array
  Handlebars.registerHelper("notContains", function (value, array, options) {
    // Ensure array is always treated as an array
    array = array instanceof Array ? array : [array];
    if (array.indexOf(value) === -1) {
      return options.fn(this);
    } else {
      return options.inverse(this);
    }
  });

  Handlebars.registerHelper("arr", function () {
    // Convert arguments to array, ommiting the last item, which is the options obect
    return Array.prototype.slice.call(arguments, 0, -1);
  });

  // Hjælper til decimal og tegn i tal
  // TODO hvad med engelsk tal format?
  Handlebars.registerHelper("numberFormat", function (value, options) {
    // Helper parameters
    var dl = options.hash["decimalLength"] || 2;
    var ts = options.hash["thousandsSep"] || ",";
    var ds = options.hash["decimalSep"] || ".";

    // Parse to float
    var value = parseFloat(value);

    // The regex
    var re = "\\d(?=(\\d{3})+" + (dl > 0 ? "\\D" : "$") + ")";

    // Formats the number with the decimals
    var num = value.toFixed(Math.max(0, ~~dl));

    // Returns the formatted number
    return (ds ? num.replace(".", ds) : num).replace(
      new RegExp(re, "g"),
      "$&" + ts
    );
  });

  // Lower case - bruges til at sikre, at filnavn på standkort er korrekte
  Handlebars.registerHelper("toLowerCase", function (str) {
    return str.toLowerCase();
  });

  Handlebars.registerHelper("divide", function (a, b) {
    return Number(a) / Number(b);
  });

  Handlebars.registerHelper("json", function (context) {
    return JSON.stringify(context);
  });

  Handlebars.registerHelper("getNested", function (obj, path) {
    // Split the path into an array of keys
    var keys = path.split(".");
    // Traverse the object using the keys
    var result = keys.reduce(function (obj, key) {
      // Return the current value if it exists, otherwise return undefined
      return obj && obj[key] !== undefined ? obj[key] : undefined;
    }, obj);
    // Return the result, which could be undefined if any key in the path was not found
    return result;
  });

  Handlebars.registerHelper("concat", function () {
    var args = Array.prototype.slice.call(arguments, 0, -1);
    return args.join("");
  });

  Handlebars.registerHelper("capitalizeFirst", function (str) {
    if (typeof str !== "string" || str.length === 0) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  });

  Handlebars.registerHelper(
    "compilePostTypesLabel",
    function (templateString, options) {
      let template = Handlebars.compile(templateString);
      let data = {
        templateOptions: options.hash.data,
        linkedModulePostTypes: options.hash.linkedModulePostType,
        element: options.hash.element,
      };
      let result = template(data);
      return new Handlebars.SafeString(result);
    }
  );
  Handlebars.registerHelper("urlEncode", function (str) {
    return encodeURIComponent(str);
  });

  Handlebars.registerHelper(
    "formatDateRange",
    function (startTimestamp, endTimestamp, options) {
      // Bestem lokalitet fra options eller brug dansk som standard
      const localeOption = options.hash.locale;
      const locale = localeOption === "en" ? enUS : da;

      const startDate = new Date(startTimestamp * 1000);
      const endDate =
        endTimestamp && endTimestamp !== startTimestamp
          ? new Date(endTimestamp * 1000)
          : null;

      const formatWithLocale = (date, formatString) =>
        format(date, formatString, { locale });

      const startWeekday = formatWithLocale(startDate, "EEEE");
      const startDay = formatWithLocale(startDate, "d");
      const startMonth = formatWithLocale(startDate, "MMMM");
      const startYear = formatWithLocale(startDate, "yyyy");

      const formatSingleStr = options.hash.formatSingle;

      let endWeekday, endDay, endMonth, endYear;
      if (endDate) {
        endWeekday = formatWithLocale(endDate, "EEEE");
        endDay = formatWithLocale(endDate, "d");
        endMonth = formatWithLocale(endDate, "MMMM");
        endYear = formatWithLocale(endDate, "yyyy");
      }

      const sameDay = endDate
        ? startDate.getDate() === endDate.getDate()
        : true;
      const sameMonth = endDate
        ? startDate.getMonth() === endDate.getMonth()
        : true;
      const sameYear = endDate
        ? startDate.getFullYear() === endDate.getFullYear()
        : true;

      const formatRangeStr = options.hash.formatRange;

      const replacePlaceholders = (str, start, end) => {
        return str
          .replace(/{startWeekday}/g, start.startWeekday)
          .replace(/{startDay}/g, start.startDay)
          .replace(/{endWeekday}/g, end ? end.endWeekday : "")
          .replace(/{endDay}/g, end ? end.endDay : "")
          .replace(/{startMonth}/g, start.startMonth)
          .replace(/{endMonth}/g, end ? end.endMonth : "")
          .replace(/{startYear}/g, start.startYear)
          .replace(/{endYear}/g, end ? end.endYear : "");
      };

      const start = { startWeekday, startDay, startMonth, startYear };
      const end = endDate ? { endWeekday, endDay, endMonth, endYear } : {};

      let result;
      if (sameDay || !endDate) {
        // Hvis der ikke er nogen slutdato eller datoerne er de samme, vis kun startdatoen
        result = replacePlaceholders(formatSingleStr, start, end);
      } else {
        // Hvis der er en slutdato, formatér datoområdet
        result = replacePlaceholders(formatRangeStr, start, end);

        if (sameMonth && sameYear) {
          // Hvis måned og år er de samme, fjern måned og år fra slutdatoen
          result = result
            .replace(` ${endMonth}`, "")
            .replace(` ${endYear}`, "");
        } else if (sameYear) {
          // Hvis kun året er det samme, fjern kun året fra slutdatoen
          result = result.replace(` ${endYear}`, "");
        }
      }

      return new Handlebars.SafeString(result.replace(/\s+/g, " ").trim());
    }
  );

  Handlebars.registerHelper("formatTime", function (timestamp, options) {
    // Bestem lokalitet fra options eller brug dansk som standard
    const locale = options.hash.locale;

    // Brug formatStr fra options eller brug et standardformat
    const formatStr = options.hash.format;

    // Konverter Unix tidsstempel til et Date objekt
    const date = new Date(timestamp * 1000);

    // Formatér datoen med den valgte lokalitet og format-streng
    return new Handlebars.SafeString(format(date, formatStr, { locale }));
  });

  Handlebars.registerHelper("joinIfBoth", function (options) {
    const first = options.hash.first;
    const second = options.hash.second;
    const joiner = options.hash.joiner || " eller ";

    const formatValue = function (value) {
      if (!value) return null;
      // Tjek om det er en email
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return new Handlebars.SafeString(
          `<a href="mailto:${Handlebars.escapeExpression(
            value
          )}">${Handlebars.escapeExpression(value)}</a>`
        );
      }
      // Tjek om det er et telefonnummer (simpel check)
      else if (/^[\d\+]+$/.test(value)) {
        return new Handlebars.SafeString(
          `<a href="tel:${Handlebars.escapeExpression(
            value
          )}">${Handlebars.escapeExpression(value)}</a>`
        );
      }
      // Ellers returner som almindelig tekst
      else {
        return Handlebars.escapeExpression(value);
      }
    };

    const parts = [];
    if (first) parts.push(formatValue(first));
    if (second) parts.push(formatValue(second));

    if (parts.length === 0) {
      return "";
    }

    return parts.join(joiner);
  });

  // Registrer helperen
  Handlebars.registerHelper("videoEmbed", function (url, options) {
    if (!url) {
      return "Ugyldig video-URL";
    }

    // YouTube regex
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    // Vimeo regex (håndterer både standard og private links)
    const vimeoRegex =
      /vimeo\.com\/(?:video\/|channels\/\w+\/|groups\/\w+\/videos\/|album\/\d+\/video\/|)(\d+)(?:\/|\?|$)/;

    let match;
    let videoId;

    // Tjek YouTube
    match = url.match(youtubeRegex);
    if (match) {
      videoId = match[1];
      return new Handlebars.SafeString(
        `<lite-youtube videoid="${videoId}" style="background-image: url('https://i.ytimg.com/vi/${videoId}/hqdefault.jpg');">
        <div class="yt-lite-playbtn"></div>
      </lite-youtube>
      <script type="module" src="https://cdn.jsdelivr.net/npm/lite-youtube-embed@latest/+esm" async></script>`
      );
    }

    // Tjek Vimeo
    match = url.match(vimeoRegex);
    if (match) {
      videoId = match[1];
      return new Handlebars.SafeString(
        `<lite-vimeo videoId="${videoId}" style="background-image: url('https://i.vimeocdn.com/video/${videoId}_640x360.jpg');">
        <div class="ltv-playbtn"></div>
      </lite-vimeo>
      <script type="module" src="https://cdn.jsdelivr.net/npm/lite-vimeo-embed@latest/+esm" async></script>`
      );
    }

    return "Videoen kunne ikke indlejes (ugyldig URL)";
  });

  Handlebars.registerHelper("formatPhone", function (phoneNumber, options) {
    // Hent mønsteret fra options.hash, eller brug standardmønsteret "## ## ####"
    const pattern = options.hash.pattern || "## ## ## ##";

    if (!phoneNumber) return "Ugyldigt telefonnummer";

    // Fjern alle ikke-cifre
    const digits = phoneNumber.toString().replace(/\D/g, "");

    let index = 0;
    let formatted = "";
    for (let i = 0; i < pattern.length; i++) {
      if (pattern[i] === "#") {
        if (index < digits.length) {
          formatted += digits[index++];
        }
      } else {
        formatted += pattern[i];
      }
    }

    return new Handlebars.SafeString(formatted);
  });
  Handlebars.registerHelper("countryName", function (countryCode, options) {
    // Hent brugerdefineret locale fra helper-kaldet, eller brug systemets standard
    const locale =
      options.hash.locale || Intl.DateTimeFormat().resolvedOptions().locale;

    if (!countryCode) {
      return new Handlebars.SafeString("Ugyldig landekode");
    }

    try {
      // Opret en Intl.DisplayNames instans for at konvertere landekoden
      const displayNames = new Intl.DisplayNames([locale], { type: "region" });
      const countryName = displayNames.of(countryCode);

      // Hvis Intl ikke kender koden, returner koden selv
      if (countryName === countryCode) {
        return new Handlebars.SafeString(countryCode);
      } else {
        return new Handlebars.SafeString(countryName);
      }
    } catch (e) {
      // Faldback hvis Intl ikke understøttes eller fejler
      return new Handlebars.SafeString(countryCode);
    }
  });
  Handlebars.registerHelper("ifAny", function () {
    // Fjern den sidste argument (options), da det er Handlebars' interne options-objekt
    const values = Array.prototype.slice.call(arguments, 0, -1);
    const options = arguments[arguments.length - 1];

    // Tjek om mindst én værdi er truthy
    const hasValue = values.some((value) => !!value);

    return hasValue ? options.fn(this) : options.inverse(this);
  });
}
